import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Save, Settings, Globe, Mail, Phone, MessageSquare } from 'lucide-react';
import { settingsAPI, partnerAPI, botSettingsAPI } from '../../utils/api';
import { toast } from 'sonner';

const AdminSettings = () => {
  const [settings, setSettings] = useState({
    site_title: '',
    site_description: '',
    contact_email: '',
    contact_phone: '',
    telegram_channel: '',
    support_chat: '',
    footer_text: ''
  });
  const [partnerProgram, setPartnerProgram] = useState({
    title: '',
    description: '',
    level1_percent: 8,
    level2_percent: 20,
    level1_description: '',
    level2_description: '',
    how_it_works: []
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newStep, setNewStep] = useState('');
  const [botSettings, setBotSettings] = useState({
    telegram_enabled: true,
    telegram_url: 'https://t.me/lngaua_bot',
    whatsapp_enabled: false,
    whatsapp_url: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [settingsRes, partnerRes, botSettingsRes] = await Promise.all([
        settingsAPI.get(),
        partnerAPI.get(),
        botSettingsAPI.get()
      ]);
      
      setSettings(settingsRes.data);
      setPartnerProgram(partnerRes.data);
      setBotSettings(botSettingsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Ошибка загрузки настроек');
    } finally {
      setLoading(false);
    }
  };

  const handleSettingsChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePartnerChange = (field, value) => {
    setPartnerProgram(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const addStep = () => {
    if (newStep.trim()) {
      setPartnerProgram(prev => ({
        ...prev,
        how_it_works: [...prev.how_it_works, newStep.trim()]
      }));
      setNewStep('');
    }
  };

  const removeStep = (index) => {
    setPartnerProgram(prev => ({
      ...prev,
      how_it_works: prev.how_it_works.filter((_, i) => i !== index)
    }));
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await settingsAPI.update(settings);
      toast.success('Настройки сайта сохранены');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Ошибка сохранения настроек');
    } finally {
      setSaving(false);
    }
  };

  const savePartnerProgram = async () => {
    setSaving(true);
    try {
      await partnerAPI.update(partnerProgram);
      toast.success('Настройки партнёрской программы сохранены');
    } catch (error) {
      console.error('Error saving partner program:', error);
      toast.error('Ошибка сохранения партнёрской программы');
    } finally {
      setSaving(false);
    }
  };

  const saveBotSettings = async () => {
    setSaving(true);
    try {
      await botSettingsAPI.update(botSettings);
      toast.success('Настройки ботов сохранены');
    } catch (error) {
      console.error('Error saving bot settings:', error);
      toast.error('Ошибка сохранения настроек ботов');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-400">Загружается...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Настройки системы</h1>
          <p className="text-gray-400">Управление глобальными настройками сайта</p>
        </div>
      </div>

      <Tabs defaultValue="site" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4 bg-gray-800">
          <TabsTrigger value="site" className="data-[state=active]:bg-blue-600">
            <Settings className="mr-2 h-4 w-4" />
            Настройки сайта
          </TabsTrigger>
          <TabsTrigger value="bots" className="data-[state=active]:bg-blue-600">
            <MessageSquare className="mr-2 h-4 w-4" />
            Боты
          </TabsTrigger>
          <TabsTrigger value="analytics" className="data-[state=active]:bg-blue-600">
            <Globe className="mr-2 h-4 w-4" />
            Аналитика
          </TabsTrigger>
          <TabsTrigger value="partner" className="data-[state=active]:bg-blue-600">
            <MessageSquare className="mr-2 h-4 w-4" />
            Партнёрская программа
          </TabsTrigger>
        </TabsList>

        <TabsContent value="site" className="space-y-6">
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center">
                <Globe className="mr-2 h-5 w-5" />
                Основные настройки сайта
              </CardTitle>
              <CardDescription className="text-gray-400">
                Настройка основной информации о сайте
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="site_title" className="text-gray-300">Название сайта</Label>
                  <Input
                    id="site_title"
                    value={settings.site_title}
                    onChange={(e) => handleSettingsChange('site_title', e.target.value)}
                    className="bg-gray-700 border-gray-600 text-white"
                    placeholder="AI-переводчик @lngaua_bot"
                    data-testid="site-title-input"
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="site_description" className="text-gray-300">Описание сайта</Label>
                  <Textarea
                    id="site_description"
                    value={settings.site_description}
                    onChange={(e) => handleSettingsChange('site_description', e.target.value)}
                    className="bg-gray-700 border-gray-600 text-white"
                    placeholder="Переводчик текста и голоса в реальном времени для Telegram"
                    rows={3}
                    data-testid="site-description-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="contact_email" className="text-gray-300">Email для связи</Label>
                  <Input
                    id="contact_email"
                    type="email"
                    value={settings.contact_email}
                    onChange={(e) => handleSettingsChange('contact_email', e.target.value)}
                    className="bg-gray-700 border-gray-600 text-white"
                    placeholder="support@example.com"
                    data-testid="contact-email-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contact_phone" className="text-gray-300">Телефон для связи</Label>
                  <Input
                    id="contact_phone"
                    value={settings.contact_phone}
                    onChange={(e) => handleSettingsChange('contact_phone', e.target.value)}
                    className="bg-gray-700 border-gray-600 text-white"
                    placeholder="+7 (999) 123-45-67"
                    data-testid="contact-phone-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="telegram_channel" className="text-gray-300">Telegram канал</Label>
                  <Input
                    id="telegram_channel"
                    value={settings.telegram_channel}
                    onChange={(e) => handleSettingsChange('telegram_channel', e.target.value)}
                    className="bg-gray-700 border-gray-600 text-white"
                    placeholder="https://t.me/channel_name"
                    data-testid="telegram-channel-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="support_chat" className="text-gray-300">Чат поддержки</Label>
                  <Input
                    id="support_chat"
                    value={settings.support_chat}
                    onChange={(e) => handleSettingsChange('support_chat', e.target.value)}
                    className="bg-gray-700 border-gray-600 text-white"
                    placeholder="https://t.me/support_chat"
                    data-testid="support-chat-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="footer_text" className="text-gray-300">Дополнительный текст в футере</Label>
                <Textarea
                  id="footer_text"
                  value={settings.footer_text}
                  onChange={(e) => handleSettingsChange('footer_text', e.target.value)}
                  className="bg-gray-700 border-gray-600 text-white"
                  placeholder="Дополнительная информация в подвале сайта"
                  rows={3}
                  data-testid="footer-text-input"
                />
              </div>

              <div className="flex justify-end">
                <Button 
                  onClick={saveSettings}
                  disabled={saving}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="save-settings-btn"
                >
                  <Save className="mr-2 h-4 w-4" />
                  {saving ? 'Сохранение...' : 'Сохранить настройки'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="bots" className="space-y-6">
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white">Настройки ботов</CardTitle>
              <CardDescription className="text-gray-400">
                Управление кнопками Telegram и WhatsApp ботов
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card className="bg-gray-700 border-gray-600">
                  <CardHeader>
                    <CardTitle className="text-white text-lg">Telegram бот</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="telegram_enabled"
                        checked={botSettings.telegram_enabled}
                        onCheckedChange={(checked) => setBotSettings(prev => ({ ...prev, telegram_enabled: checked }))}
                      />
                      <Label htmlFor="telegram_enabled" className="text-gray-300">Показывать кнопку Telegram</Label>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-300">URL Telegram бота</Label>
                      <Input
                        value={botSettings.telegram_url}
                        onChange={(e) => setBotSettings(prev => ({ ...prev, telegram_url: e.target.value }))}
                        className="bg-gray-600 border-gray-500 text-white"
                        placeholder="https://t.me/your_bot"
                      />
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gray-700 border-gray-600">
                  <CardHeader>
                    <CardTitle className="text-white text-lg">WhatsApp бот</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="whatsapp_enabled"
                        checked={botSettings.whatsapp_enabled}
                        onCheckedChange={(checked) => setBotSettings(prev => ({ ...prev, whatsapp_enabled: checked }))}
                      />
                      <Label htmlFor="whatsapp_enabled" className="text-gray-300">Показывать кнопку WhatsApp</Label>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-300">URL WhatsApp бота</Label>
                      <Input
                        value={botSettings.whatsapp_url}
                        onChange={(e) => setBotSettings(prev => ({ ...prev, whatsapp_url: e.target.value }))}
                        className="bg-gray-600 border-gray-500 text-white"
                        placeholder="https://wa.me/your_bot"
                      />
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="flex justify-end">
                <Button 
                  onClick={saveBotSettings}
                  disabled={saving}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Save className="mr-2 h-4 w-4" />
                  {saving ? 'Сохранение...' : 'Сохранить настройки ботов'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="analytics" className="space-y-6">
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white">Счётчики аналитики</CardTitle>
              <CardDescription className="text-gray-400">
                Настройка кодов Яндекс.Метрики и Google Analytics
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="yandex_metrika" className="text-gray-300">ID Яндекс.Метрики</Label>
                  <Input
                    id="yandex_metrika"
                    value={settings.yandex_metrika}
                    onChange={(e) => handleSettingsChange('yandex_metrika', e.target.value)}
                    className="bg-gray-700 border-gray-600 text-white"
                    placeholder="12345678"
                    data-testid="yandex-metrika-input"
                  />
                  <p className="text-xs text-gray-500">Введите только ID счётчика (цифры)</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="google_analytics" className="text-gray-300">ID Google Analytics</Label>
                  <Input
                    id="google_analytics"
                    value={settings.google_analytics}
                    onChange={(e) => handleSettingsChange('google_analytics', e.target.value)}
                    className="bg-gray-700 border-gray-600 text-white"
                    placeholder="G-XXXXXXXXXX"
                    data-testid="google-analytics-input"
                  />
                  <p className="text-xs text-gray-500">Введите измерение GA4 (G-XXXXXXXXXX)</p>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="site_keywords" className="text-gray-300">SEO ключевые слова</Label>
                <Textarea
                  id="site_keywords"
                  value={settings.site_keywords}
                  onChange={(e) => handleSettingsChange('site_keywords', e.target.value)}
                  className="bg-gray-700 border-gray-600 text-white"
                  placeholder="AI переводчик, перевод голоса, телеграм бот, переводчик текста"
                  rows={3}
                  data-testid="site-keywords-input"
                />
              </div>

              <div className="flex justify-end">
                <Button 
                  onClick={saveSettings}
                  disabled={saving}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="save-analytics-btn"
                >
                  <Save className="mr-2 h-4 w-4" />
                  {saving ? 'Сохранение...' : 'Сохранить аналитику'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="partner" className="space-y-6">
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white">Партнёрская программа</CardTitle>
              <CardDescription className="text-gray-400">
                Настройка информации о партнёрской программе
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="partner_title" className="text-gray-300">Заголовок</Label>
                  <Input
                    id="partner_title"
                    value={partnerProgram.title}
                    onChange={(e) => handlePartnerChange('title', e.target.value)}
                    className="bg-gray-700 border-gray-600 text-white"
                    placeholder="Партнёрская программа"
                    data-testid="partner-title-input"
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="partner_description" className="text-gray-300">Описание</Label>
                  <Input
                    id="partner_description"
                    value={partnerProgram.description}
                    onChange={(e) => handlePartnerChange('description', e.target.value)}
                    className="bg-gray-700 border-gray-600 text-white"
                    placeholder="Двухуровневая партнёрка с высокими комиссиями"
                    data-testid="partner-description-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-white">1-й уровень</h3>
                  <div className="space-y-2">
                    <Label htmlFor="level1_percent" className="text-gray-300">Процент комиссии</Label>
                    <Input
                      id="level1_percent"
                      type="number"
                      value={partnerProgram.level1_percent}
                      onChange={(e) => handlePartnerChange('level1_percent', parseInt(e.target.value))}
                      className="bg-gray-700 border-gray-600 text-white"
                      data-testid="level1-percent-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="level1_description" className="text-gray-300">Описание</Label>
                    <Textarea
                      id="level1_description"
                      value={partnerProgram.level1_description}
                      onChange={(e) => handlePartnerChange('level1_description', e.target.value)}
                      className="bg-gray-700 border-gray-600 text-white"
                      rows={3}
                      data-testid="level1-description-input"
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-white">2-й уровень</h3>
                  <div className="space-y-2">
                    <Label htmlFor="level2_percent" className="text-gray-300">Процент комиссии</Label>
                    <Input
                      id="level2_percent"
                      type="number"
                      value={partnerProgram.level2_percent}
                      onChange={(e) => handlePartnerChange('level2_percent', parseInt(e.target.value))}
                      className="bg-gray-700 border-gray-600 text-white"
                      data-testid="level2-percent-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="level2_description" className="text-gray-300">Описание</Label>
                    <Textarea
                      id="level2_description"
                      value={partnerProgram.level2_description}
                      onChange={(e) => handlePartnerChange('level2_description', e.target.value)}
                      className="bg-gray-700 border-gray-600 text-white"
                      rows={3}
                      data-testid="level2-description-input"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <Label className="text-gray-300">Как это работает</Label>
                <div className="flex gap-2">
                  <Input
                    value={newStep}
                    onChange={(e) => setNewStep(e.target.value)}
                    className="bg-gray-700 border-gray-600 text-white flex-1"
                    placeholder="Добавить шаг"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addStep())}
                    data-testid="add-step-input"
                  />
                  <Button type="button" onClick={addStep} variant="outline" className="border-gray-600">
                    Добавить
                  </Button>
                </div>
                
                {partnerProgram.how_it_works.length > 0 && (
                  <div className="space-y-2">
                    {partnerProgram.how_it_works.map((step, index) => (
                      <div key={index} className="flex items-center justify-between bg-gray-700 p-3 rounded">
                        <span className="text-white">{index + 1}. {step}</span>
                        <Button
                          type="button"
                          onClick={() => removeStep(index)}
                          variant="ghost"
                          size="sm"
                          className="text-red-400 hover:text-red-300"
                        >
                          Удалить
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex justify-end">
                <Button 
                  onClick={savePartnerProgram}
                  disabled={saving}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="save-partner-btn"
                >
                  <Save className="mr-2 h-4 w-4" />
                  {saving ? 'Сохранение...' : 'Сохранить партнёрскую программу'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminSettings;